'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';
import { submitLeadMagnetDownload } from '@/lib/leadService';

interface LeadMagnetLibraryProps {
  currentLanguage?: string;
}

interface Resource {
  id: string;
  title: string;
  description: string;
  type: string;
  pipeline: string;
  audience: string;
  pages: string;
  icon: string;
  color: string;
  tags: string[];
  downloadUrl: string;
}

const resources: Resource[] = [
  {
    id: 'brsr-lite-checklist',
    title: 'BRSR Lite Checklist 2025',
    description: 'Complete step-by-step checklist for value chain vendors to achieve BRSR Lite compliance. Covers all 9 essential disclosures, scoring matrix, and empanelment readiness guide.',
    type: 'PDF Checklist',
    pipeline: 'B',
    audience: 'Vendors, IT Firms, CAs',
    pages: '12 pages',
    icon: 'DocumentCheckIcon',
    color: 'amber',
    tags: ['BRSR Lite', 'Vendor', 'Compliance', 'Free'],
    downloadUrl: '#brsr-lite-checklist',
  },
  {
    id: 'net-zero-audit-guide',
    title: 'Net Zero Readiness Audit Guide',
    description: 'Comprehensive audit framework for enterprises and PSUs. Includes GHG Protocol baseline methodology, Net Zero pathway templates, and CAQSG™ MRV integration checklist.',
    type: 'PDF Guide',
    pipeline: 'A',
    audience: 'Enterprises, PSUs, Data Centers',
    pages: '18 pages',
    icon: 'DocumentMagnifyingGlassIcon',
    color: 'emerald',
    tags: ['Net Zero', 'Enterprise', 'MRV', 'Free'],
    downloadUrl: '#net-zero-audit-guide',
  },
  {
    id: 'carbon-credits-farm-guide',
    title: 'Carbon Credits for Farm & Land Owners',
    description: 'Simple guide for GenZ referring their family\'s ancestral land or farm for carbon credit generation. Explains the process, timelines, earnings potential, and how to get started.',
    type: 'PDF Guide',
    pipeline: 'B',
    audience: 'Farm Owners, Ancestral Land',
    pages: '8 pages',
    icon: 'HomeModernIcon',
    color: 'green',
    tags: ['Carbon Credits', 'Farm', 'Family Land', 'Free'],
    downloadUrl: '#carbon-credits-farm-guide',
  },
  {
    id: 'brsr-ai-automation-guide',
    title: 'BRSR AI Automation for CAs & Consultants',
    description: 'How CAs and consulting firms can leverage GenCarbon\'s AI-powered BRSR automation to serve more clients, reduce reporting time by 70%, and build a recurring ESG revenue stream.',
    type: 'PDF Playbook',
    pipeline: 'A',
    audience: 'CAs, Consultants, BRSR Firms',
    pages: '14 pages',
    icon: 'CpuChipIcon',
    color: 'violet',
    tags: ['BRSR AI', 'CA', 'Automation', 'Free'],
    downloadUrl: '#brsr-ai-automation-guide',
  },
];

const colorMap: Record<string, { bg: string; border: string; text: string; badge: string; btn: string }> = {
  amber: {
    bg: 'bg-amber-500/10',
    border: 'border-amber-500/30',
    text: 'text-amber-400',
    badge: 'bg-amber-500/20 text-amber-300',
    btn: 'bg-amber-500 hover:bg-amber-400',
  },
  emerald: {
    bg: 'bg-emerald-500/10',
    border: 'border-emerald-500/30',
    text: 'text-emerald-400',
    badge: 'bg-emerald-500/20 text-emerald-300',
    btn: 'bg-emerald-500 hover:bg-emerald-400',
  },
  green: {
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    text: 'text-green-400',
    badge: 'bg-green-500/20 text-green-300',
    btn: 'bg-green-500 hover:bg-green-400',
  },
  violet: {
    bg: 'bg-violet-500/10',
    border: 'border-violet-500/30',
    text: 'text-violet-400',
    badge: 'bg-violet-500/20 text-violet-300',
    btn: 'bg-violet-500 hover:bg-violet-400',
  },
};

function DownloadModal({ resource, onClose }: { resource: Resource; onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const colors = colorMap[resource.color];

  const handleDownload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }
    setIsSubmitting(true);
    setError('');

    try {
      const result = await submitLeadMagnetDownload({
        email,
        fullName: name,
        resourceName: resource.title,
        resourceType: resource.type,
        pipeline: resource.pipeline,
      });

      if (!result.success) {
        throw new Error(result.error || 'Failed to process download');
      }

      trackEvent('lead_magnet_download', {
        event_category: 'Lead Magnet',
        event_label: resource.title,
        resource_id: resource.id,
        pipeline: resource.pipeline,
        value: 1,
      });

      setSuccess(true);
    } catch (err: any) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose}>
      <div
        className="bg-slate-900 rounded-3xl p-6 sm:p-8 max-w-md w-full border border-white/10 shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        {success ? (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <Icon name="CheckCircleIcon" size={32} className="text-emerald-400" />
            </div>
            <h3 className="text-xl font-bold text-white mb-2">Download Ready! 🎉</h3>
            <p className="text-slate-300 text-sm mb-4">
              <strong className="text-emerald-300">{resource.title}</strong> has been sent to <strong className="text-white">{email}</strong>
            </p>
            <p className="text-slate-400 text-xs mb-6">
              Check your inbox (and spam folder). Our team will also follow up within 24 hours.
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2.5 bg-emerald-500 text-white font-semibold rounded-xl hover:bg-emerald-400 transition-all"
            >
              Close
            </button>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between mb-5">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 ${colors.bg} rounded-xl flex items-center justify-center`}>
                  <Icon name={resource.icon as any} size={20} className={colors.text} />
                </div>
                <div>
                  <p className={`text-xs font-bold uppercase tracking-widest ${colors.text}`}>{resource.type}</p>
                  <h3 className="text-base font-bold text-white leading-tight">{resource.title}</h3>
                </div>
              </div>
              <button onClick={onClose} className="text-slate-400 hover:text-white transition-colors ml-2 flex-shrink-0">
                <Icon name="XMarkIcon" size={20} />
              </button>
            </div>

            <p className="text-slate-300 text-sm mb-5">{resource.description}</p>

            <form onSubmit={handleDownload} className="space-y-3">
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Your Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1">Email Address *</label>
                <input
                  type="email"
                  value={email}
                  onChange={e => { setEmail(e.target.value); setError(''); }}
                  placeholder="you@company.com"
                  className={`w-full px-4 py-3 bg-white/10 border rounded-xl text-white placeholder-slate-500 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-400 transition-all ${error ? 'border-red-400' : 'border-white/20'}`}
                />
                {error && <p className="text-red-400 text-xs mt-1">{error}</p>}
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full py-3.5 ${colors.btn} text-white font-bold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm active:scale-95 disabled:opacity-60`}
              >
                {isSubmitting ? (
                  <><Icon name="ArrowPathIcon" size={18} className="animate-spin" /> Processing...</>
                ) : (
                  <><Icon name="ArrowDownTrayIcon" size={18} /> Download Free Resource</>
                )}
              </button>
              <p className="text-center text-xs text-slate-500">
                🔒 No spam · <a href="https://www.gencarbon.in" className="text-emerald-400">www.gencarbon.in</a>
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

export default function LeadMagnetLibrary({ currentLanguage = 'en' }: LeadMagnetLibraryProps) {
  const [selectedResource, setSelectedResource] = useState<Resource | null>(null);
  const [activeFilter, setActiveFilter] = useState<'all' | 'A' | 'B'>('all');

  const filteredResources = activeFilter === 'all'
    ? resources
    : resources.filter(r => r.pipeline === activeFilter);

  const handleResourceClick = (resource: Resource) => {
    setSelectedResource(resource);
    trackEvent('lead_magnet_view', {
      event_category: 'Lead Magnet',
      event_label: resource.title,
      resource_id: resource.id,
    });
  };

  return (
    <>
      <section id="lead-magnet-library" className="py-16 sm:py-20 bg-slate-900">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">

          {/* Header */}
          <div className="text-center mb-10 sm:mb-12">
            <div className="inline-flex items-center gap-2 px-4 sm:px-5 py-2 bg-violet-500/10 rounded-full mb-5 border border-violet-500/20">
              <Icon name="BookOpenIcon" size={16} className="text-violet-400" />
              <span className="text-xs sm:text-sm font-semibold text-violet-400 uppercase tracking-widest">Free Resource Library</span>
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3">
              Download Free <span className="text-violet-400">ESG Guides</span>
            </h2>
            <p className="text-base sm:text-xl text-slate-400 max-w-2xl mx-auto">
              Practical resources for every audience — from farm owners to enterprise CXOs. All free, no credit card required.
            </p>
          </div>

          {/* Filter Tabs */}
          <div className="flex justify-center mb-8">
            <div className="bg-white/5 rounded-2xl p-1.5 flex gap-1.5 border border-white/10">
              {[
                { key: 'all', label: 'All Resources' },
                { key: 'A', label: '🏢 Enterprise' },
                { key: 'B', label: '🌾 Vendors & Farms' },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => setActiveFilter(f.key as any)}
                  className={`px-3 sm:px-5 py-2 rounded-xl text-xs sm:text-sm font-semibold transition-all duration-200 ${
                    activeFilter === f.key
                      ? 'bg-violet-500 text-white shadow-lg'
                      : 'text-slate-400 hover:text-white hover:bg-white/10'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {/* Resource Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {filteredResources.map(resource => {
              const colors = colorMap[resource.color];
              return (
                <div
                  key={resource.id}
                  className={`group relative bg-white/5 backdrop-blur-sm rounded-2xl p-5 sm:p-6 border ${colors.border} hover:border-opacity-60 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 cursor-pointer flex flex-col`}
                  onClick={() => handleResourceClick(resource)}
                >
                  {/* Free badge */}
                  <div className="absolute top-4 right-4">
                    <span className="text-xs font-bold px-2 py-1 bg-emerald-500/20 text-emerald-300 rounded-full border border-emerald-400/20">
                      FREE
                    </span>
                  </div>

                  {/* Icon */}
                  <div className={`w-12 h-12 ${colors.bg} rounded-2xl flex items-center justify-center mb-4`}>
                    <Icon name={resource.icon as any} size={24} className={colors.text} />
                  </div>

                  {/* Content */}
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${colors.badge}`}>
                        Pipeline {resource.pipeline}
                      </span>
                      <span className="text-xs text-slate-500">{resource.pages}</span>
                    </div>
                    <h3 className="text-base font-bold text-white mb-2 leading-tight">{resource.title}</h3>
                    <p className="text-slate-400 text-xs leading-relaxed mb-3 line-clamp-3">{resource.description}</p>

                    {/* Audience */}
                    <p className="text-xs text-slate-500 mb-3">
                      <span className="text-slate-400 font-medium">For:</span> {resource.audience}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {resource.tags.map(tag => (
                        <span key={tag} className="text-xs px-2 py-0.5 bg-white/5 text-slate-400 rounded-full border border-white/10">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>

                  {/* CTA */}
                  <button
                    className={`w-full py-2.5 ${colors.btn} text-white font-semibold rounded-xl transition-all duration-300 flex items-center justify-center gap-2 text-sm group-hover:shadow-lg active:scale-95`}
                    onClick={e => { e.stopPropagation(); handleResourceClick(resource); }}
                  >
                    <Icon name="ArrowDownTrayIcon" size={16} />
                    Download Free
                  </button>
                </div>
              );
            })}
          </div>

          {/* Bottom CTA */}
          <div className="text-center mt-10">
            <p className="text-slate-400 text-sm">
              Need a custom resource? Email us at{' '}
              <a href="mailto:nupurbanker@gmail.com" className="text-emerald-400 hover:underline font-semibold">
                nupurbanker@gmail.com
              </a>
              {' '}· <a href="https://www.gencarbon.in" className="text-emerald-400 hover:underline">www.gencarbon.in</a>
            </p>
          </div>
        </div>
      </section>

      {/* Download Modal */}
      {selectedResource && (
        <DownloadModal
          resource={selectedResource}
          onClose={() => setSelectedResource(null)}
        />
      )}
    </>
  );
}
